import React from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, ArrowLeft, Shield } from 'lucide-react';
import { useSelector } from 'react-redux';
import { Button } from '@/lib/components/ui/button';
import { getOrganizationId } from '@/data/store/selectors';

export const AdminLayout: React.FC = () => {
  const navigate = useNavigate();
  // Developers can reach the CAS dashboard straight from the selection screen,
  // before joining an organization; there is no app to go back to in that case.
  const organizationId = useSelector(getOrganizationId);

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 font-sans">
      {/* Sidebar */}
      <aside className="w-72 border-r border-gray-200/60 bg-white/70 backdrop-blur-xl flex flex-col shadow-[4px_0_24px_rgba(0,0,0,0.02)] z-10">
        <div className="p-8 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 shadow-lg shadow-blue-500/20">
              <Shield className="h-5 w-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">CAS Admin</h2>
              <p className="text-xs text-gray-500 font-medium">Central Administration</p>
            </div>
          </div>
        </div>
        
        <nav className="flex-1 px-4 space-y-1.5 mt-4">
          <NavLink
            to="/admin/organizations"
            className={({ isActive }) =>
              `group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive 
                  ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100/50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'}`}>
                  <LayoutDashboard className="w-4 h-4" />
                </div>
                <span className="font-medium">Organizations</span>
              </>
            )}
          </NavLink>
          
          <NavLink
            to="/admin/developers"
            className={({ isActive }) =>
              `group flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 ${
                isActive 
                  ? 'bg-blue-50 text-blue-700 shadow-sm border border-blue-100/50' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 border border-transparent'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${isActive ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500 group-hover:bg-gray-200'}`}>
                  <Users className="w-4 h-4" />
                </div>
                <span className="font-medium">Developers</span>
              </>
            )}
          </NavLink>
        </nav>

        <div className="p-6 border-t border-gray-100">
          <Button 
            variant="outline" 
            className="w-full justify-start gap-3 h-11 rounded-xl border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-900" 
            onClick={() => navigate(organizationId ? '/dashboard' : '/organizations')}
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">{organizationId ? 'Back to App' : 'Back to Organizations'}</span>
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto relative">
        <div className="absolute inset-0 bg-grid-slate-100/[0.04] bg-[bottom_1px_center]" />
        <div className="p-10 max-w-7xl mx-auto relative z-10">
          <Outlet />
        </div>
      </main>
    </div>
  );
};
